<template>
	<BaseModal :title="t('title')" v-model="model" noPadding>
		<div class="px-5 pb-5">
			<div class="flex flex-col gap-4">
				<InventoryItem
					v-for="item in claimableItems"
					alt
					:src="icon(item.modelUuid)"
					:collection="item.collectionName"
					:name="item.modelName"
					:price="item.price"
					:preloader="item.preloader"
				>
					<div
						class="relative after:absolute after:-inset-3"
						v-hover="{ active: 'scale-90', impact: 'soft' }"
						@click="removeItem(item.uuid)"
					>
						<TrashIcon class="text-label-tertiary h-5 w-5" />
					</div>
				</InventoryItem>
			</div>
		</div>
		<BottomActionsModal class="pt-4">
			<CountAndTotal :count="claimableItems.length" :total="amountCoins" />
			<BaseButton
				size="lg"
				color="primary"
				class="relative mt-3 w-full"
				@click="claimGifts()"
			>
				<div>{{ t('claim') }}</div>
			</BaseButton>
			<div
				class="text-label-tertiary mt-3 text-center text-xs"
				v-if="unclaimableCount"
			>
				{{ t('unclaimable', unclaimableCount) }}
			</div>
		</BottomActionsModal>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import InventoryItem from '../InventoryItem.vue'
import { icon } from '@/utils/urls'
import TrashIcon from '@/assets/icons/trash.svg'
import { useI18n } from 'vue-i18n'
import BottomActionsModal from '../BottomActionsModal.vue'
import { useInventory } from '@/stores/inventory'
import CountAndTotal from '../CountAndTotal.vue'
import { useToast } from '@/stores/toast'

const { t } = useI18n({
	messages: {
		en: {
			title: 'claim gifts',
			claim: 'claim now',
			unclaimable:
				'1 other gift cannot be claimed | {n} other gifts cannot be claimed',
			failedToClaim: 'Failed to claim selected gifts'
		},
		ru: {
			title: 'вывод гифтов',
			claim: 'вывести сейчас',
			unclaimable:
				'все гифты можно вывести | {n} другой гифт не может быть выведен | {n} других гифта не могут быть выведены | {n} других гифтов не могут быть выведены',
			failedToClaim: 'Не удалось вывести выбранные гифты'
		}
	}
})

const props = defineProps<{
	items: {
		uuid: string
		modelUuid: string
		collectionName: string
		modelName: string
		price: number
		preloader?: string | null
		claimable: boolean
	}[]
	all?: boolean
}>()

const emit = defineEmits<{
	(e: 'removeItem', uuid: string): void
	(e: 'claimed'): void
}>()

const model = defineModel<boolean>()

watch(
	() => props.items,
	() => {
		if (props.items.length > 0) {
			return
		}

		model.value = false
	}
)

const amountCoins = computed(() =>
	props.items.reduce((acc, item) => acc + item.price, 0)
)

const claimableItems = computed(() =>
	props.items.filter((item) => item.claimable)
)

const unclaimableCount = computed(
	() => props.items.length - claimableItems.value.length
)

const removeItem = (uuid: string) => {
	emit('removeItem', uuid)
}

const inventory = useInventory()
const toast = useToast()

const claimGifts = async () => {
	emit('claimed')

	model.value = false

	const result = await inventory.claimItems(
		claimableItems.value.map((item) => item.uuid)
	)

	if (!result) {
		toast.add({
			type: 'error',
			text: t('failedToClaim')
		})
	}
}
</script>
